"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const categories = ["All", "Concrete", "Masonry", "Structural"];
const projects = [{
  id: 1,
  title: "Industrial Foundation",
  category: "Structural",
  client: "Metro Logistics",
  year: "2023",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_5457.png?v=v5h55m9862l",
  aspectClass: "aspect-[3/4]"
}, {
  id: 2,
  title: "Custom Stone Patio",
  category: "Masonry",
  client: "Private Residence",
  year: "2024",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/stamped_concrete_project.png?v=mj8zj3uny9j",
  aspectClass: "aspect-square"
}, {
  id: 3,
  title: "Polished Slab Floor",
  category: "Concrete",
  client: "Tech Hub HQ",
  year: "2023",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_4239.jpg?v=fsmx6fsba7b",
  aspectClass: "aspect-[4/3]"
}, {
  id: 4,
  title: "Retaining Wall System",
  category: "Masonry",
  client: "Hillside Estates",
  year: "2024",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_3742-scaled.jpg?v=pfexyoza3rm",
  aspectClass: "aspect-[3/4]"
}, {
  id: 5,
  title: "Reinforced Driveway",
  category: "Concrete",
  client: "Commercial Plaza",
  year: "2023",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/File_001-1.png?v=zam6wmqt4s",
  aspectClass: "aspect-square"
}, {
  id: 6,
  title: "Structural Columns",
  category: "Structural",
  client: "Urban Lofts",
  year: "2024",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_5455.webp?v=jcuyv1y9or7",
  aspectClass: "aspect-[4/3]"
}, {
  id: 7,
  title: "decorative patio",
  category: "Concrete",
  client: "Logistics Center",
  year: "2023",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_3213.jpg?v=olidg95tos8",
  aspectClass: "aspect-[3/4]"
}, {
  id: 8,
  title: "decorative patio",
  category: "Concrete",
  client: "City Park",
  year: "2024",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_4234.jpg?v=oaln6rvous",
  aspectClass: "aspect-square"
}, {
  id: 9,
  title: "Parking Garage Deck",
  category: "Concrete",
  client: "Municipal Services",
  year: "2023",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_5487-scaled.png?v=sb0ct601ddc",
  aspectClass: "aspect-[4/3]"
}, {
  id: 10,
  title: "Residential Foundation",
  category: "Concrete",
  client: "Custom Home Builder",
  year: "2024",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_5455.png?v=ooctjhgawf",
  aspectClass: "aspect-[3/4]"
}, {
  id: 11,
  title: "Exposed Aggregate Patio",
  category: "Concrete",
  client: "Private Estate",
  year: "2023",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_4077-scaled.jpg?v=4kl8kh0x4c5",
  aspectClass: "aspect-square"
}, {
  id: 12,
  title: "Loading Dock Ramp",
  category: "Concrete",
  client: "Distribution Hub",
  year: "2024",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/sampson-concrete-and-masory-basement-entrance-stairs-scaled.jpg?v=zisxnsarmmm",
  aspectClass: "aspect-[4/3]"
}, {
  id: 13,
  title: "Stamped Concrete Entry",
  category: "Concrete",
  client: "Luxury Hotel",
  year: "2023",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_4371-2.png?v=ig3jcs8o1kk",
  aspectClass: "aspect-[3/4]"
}, {
  id: 14,
  title: "Curbs and Gutters",
  category: "Concrete",
  client: "Subdivision Dev",
  year: "2024",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_4348.jpg?v=83zqqvbxnax",
  aspectClass: "aspect-square"
}, {
  id: 15,
  title: "Industrial Containment",
  category: "Concrete",
  client: "Chemical Plant",
  year: "2023",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_3212.jpg?v=x0x64z0tnza",
  aspectClass: "aspect-[4/3]"
}, {
  id: 16,
  title: "Pool Deck Surfacing",
  category: "Concrete",
  client: "Recreation Center",
  year: "2024",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_4159-scaled.jpg?v=kn3qmysl6j",
  aspectClass: "aspect-[3/4]"
}, {
  id: 17,
  title: "Modern Concrete Driveway",
  category: "Concrete",
  client: "Suburban Residence",
  year: "2024",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_4354.png?v=br7e7lfdez6",
  aspectClass: "aspect-square"
}, {
  id: 18,
  title: "Heavy-Duty Access Road",
  category: "Concrete",
  client: "Industrial Park",
  year: "2023",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1621168/img/concrete_driveway_industrial.png",
  aspectClass: "aspect-[4/3]"
}, {
  id: 19,
  title: "Curved Residential Driveway",
  category: "Concrete",
  client: "Private Client",
  year: "2024",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/concrete-driveway.jpeg?v=14phg318e8",
  aspectClass: "aspect-[3/4]"
}, {
  id: 20,
  title: "Textured Concrete Entrance",
  category: "Concrete",
  client: "Estate Development",
  year: "2023",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_4347.jpg?v=ch3v7ixw67u",
  aspectClass: "aspect-square"
}, {
  id: 21,
  title: "Reinforced Parking Pad",
  category: "Concrete",
  client: "Retail Center",
  year: "2024",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/warehouse_concrete_slab.png?v=ow9cbw5g7dm",
  aspectClass: "aspect-[4/3]"
}, {
  id: 22,
  title: "Natural Stone Walkway",
  category: "Masonry",
  client: "Lakeside Manor",
  year: "2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1621168/img/masonry_photo1.png",
  aspectClass: "aspect-square"
}, {
  id: 23,
  title: "Brick Facade Restoration",
  category: "Masonry",
  client: "Heritage Lofts",
  year: "2023",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/industrial_foundations_site.webp?v=f6zc2j967p",
  aspectClass: "aspect-[3/4]"
}, {
  id: 24,
  title: "Outdoor Stone Fireplace",
  category: "Masonry",
  client: "Mountain Retreat",
  year: "2024",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_4204-1-scaled.jpg?v=iepxtaef7cb",
  aspectClass: "aspect-[4/3]"
}, {
  id: 25,
  title: "Flagstone Pool Surround",
  category: "Masonry",
  client: "Coastal Villa",
  year: "2023",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_3044.png?v=gdbf0kj8qpo",
  aspectClass: "aspect-square"
}, {
  id: 26,
  title: "Custom Brick Archway",
  category: "Masonry",
  client: "Estate Gardens",
  year: "2024",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_4220-scaled.jpg?v=yieqp6u73b",
  aspectClass: "aspect-[3/4]"
}, {
  id: 27,
  title: "Granite Retaining Wall",
  category: "Masonry",
  client: "Civic Plaza",
  year: "2023",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/post_project-78ee849a.png?v=48mjxcftmv7",
  aspectClass: "aspect-[4/3]"
}, {
  id: 28,
  title: "Limestone Entry Steps",
  category: "Masonry",
  client: "Corporate HQ",
  year: "2024",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/Untitled-design-2.png?v=4vj38bpkvng",
  aspectClass: "aspect-square"
}, {
  id: 29,
  title: "Decorative Brick Paving",
  category: "Masonry",
  client: "Town Square",
  year: "2023",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/Untitled-design1.png?v=r5e2djqf1dh",
  aspectClass: "aspect-[3/4]"
}, {
  id: 30,
  title: "Slate Patio Installation",
  category: "Masonry",
  client: "Private Residence",
  year: "2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1621168/img/masonry_photo9.png",
  aspectClass: "aspect-[4/3]"
}, {
  id: 31,
  title: "Stone Pillar Construction",
  category: "Masonry",
  client: "Gated Community",
  year: "2023",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/gallery_stamped_patio.webp?v=pd26u1yzlyb",
  aspectClass: "aspect-square"
}, {
  id: 32,
  title: "Structural Concrete Foundation",
  category: "Concrete",
  client: "Apex Industrial",
  year: "2024",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/File_000.png?v=0k2my1f0765l",
  aspectClass: "aspect-[3/4]"
}, {
  id: 33,
  title: "Commercial Grade Slab",
  category: "Concrete",
  client: "Logistics Hub",
  year: "2023",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_4370.png?v=j7z8lbo39di",
  aspectClass: "aspect-square"
}, {
  id: 34,
  title: "Reinforced Retaining Wall",
  category: "Concrete",
  client: "Civic Center",
  year: "2024",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_4232.jpg?v=jpoigry5dc",
  aspectClass: "aspect-[4/3]"
}, {
  id: 35,
  title: "Polished Interior Floor",
  category: "Concrete",
  client: "Modern Office Park",
  year: "2023",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_4355-1.png?v=alzxkjr07qd",
  aspectClass: "aspect-[3/4]"
}, {
  id: 36,
  title: "Modern Concrete Walkway",
  category: "Concrete",
  client: "Residential Complex",
  year: "2024",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_5411.png?v=jonf8e3kq6",
  aspectClass: "aspect-square"
}];
export default function Portfolio() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  return <section data-section-id="225389" id="photogallery" className="bg-background text-foreground py-32 md:py-48 relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`max-w-4xl mx-auto text-center mb-20 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-default font-bold tracking-tighter text-primary uppercase mb-6">
            Project <span className="text-foreground">Gallery</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default max-w-2xl mx-auto">
            Showcasing our commitment to durability and precision craftsmanship across residential and commercial landscapes.
          </p>
        </div>

        <div ref={gridRef} className={`transition-all duration-1000 delay-200 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Tabs defaultValue="All" className="w-full">
            <div className="flex justify-center mb-16">
              <TabsList className="bg-muted border border-border rounded-none p-1.5 h-auto backdrop-blur-md">
                {categories.map((cat, i) => <TabsTrigger key={cat} value={cat} data-index={i} className="rounded-none px-6 py-2.5 text-sm md:text-base font-default font-bold transition-all data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-muted-foreground hover:text-foreground">
                    {cat}
                  </TabsTrigger>)}
              </TabsList>
            </div>

            {categories.map((cat, i) => <TabsContent key={cat} value={cat} data-index={i} className="mt-0 outline-none focus-visible:ring-0">
                <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
                  {projects.filter(p => cat === "All" || p.category === cat).map((project, j) => <div key={project.id} data-index={j} className="group relative overflow-hidden rounded-none border-t-4 border-t-accent bg-card shadow-md break-inside-avoid">
                        <Link to="/" className="block w-full h-full">
                          <div className={`w-full relative overflow-hidden ${project.aspectClass}`}>
                            <img src={project.image} alt={project.id === 12 ? "decorative patio" : project.title} className="object-cover w-full h-full grayscale transition-all duration-700 ease-out group-hover:scale-105 !bg-transparent !text-[hsl(0,69%,56%)] hover:grayscale-0" loading="lazy" />
                          </div>
                        </Link>
                      </div>)}
                </div>
              </TabsContent>)}
          </Tabs>
        </div>
      </div>
    </section>;
}